import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  Pagination,
  usePagination,
  useUpdateQueryParams,
  GetColorStatus,
  withStyles,
  PartnerPartnersAccountStatus,
} from 'vopay';
import { Button, TableFiltersContainer, TransactionStatus, Table, Title } from 'UI';
import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useFetchPartnerPartners from '@/hooks/useApi/useFetchPartnerPartners';
import PartnerListSearchForm from './PartnerListSearchForm';
import styles from './PartnerList.module.scss';

const cn = withStyles(styles);

const PAGE_SIZE = 20;

type QueryParams = {
  status: PartnerPartnersAccountStatus | '';
  searchText: string;
};

export default function PartnerList() {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.List' });
  const { t: tStatus } = useTranslation('translations', { keyPrefix: 'Global.PartnerAccountStatus' });
  const { partners, paginationMetaData, fetchPartnerPartners } = useFetchPartnerPartners();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const status = useMemo(() => (queryParams.get('status') as PartnerPartnersAccountStatus) || '', [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);

  const filtersApplied = useMemo(() => !!status || !!searchText, [status, searchText]);

  const { pageCount, pageIndex, setPageIndex, offset } = usePagination(paginationMetaData?.TotalRecords || 0, {
    pageSize: PAGE_SIZE,
  });

  useEffect(() => {
    const loadPartners = async () => {
      try {
        setDataFetchingInfo(true, '', 'fullScreen');
        await fetchPartnerPartners({
          AccountStatus: status || undefined,
          SearchText: searchText || undefined,
          Offset: offset,
          Limit: PAGE_SIZE,
        });
      } catch (error) {
        alertDialog.error(
          t('errorTitle', { defaultValue: 'Unable to Load Partners' }),
          error instanceof Error ? error.message : '',
        );
      } finally {
        setDataFetchingInfo(false);
      }
    };

    void loadPartners();
  }, [status, searchText, offset]);

  return (
    <Box flx="col" gapSize="36px">
      <Box jc="sb">
        <Title>{t('title', { defaultValue: 'Partner List' })}</Title>
        <Button
          id="partners-addPartner"
          colorScheme="primary"
          disabled={isRoleViewOnly}
          onClick={() => history.push('/partners-create')}
        >
          {t('addPartnerButton', { defaultValue: 'Add Partner' })}
        </Button>
      </Box>

      <Card style={{ padding: '16px' }}>
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />
          <TableFiltersContainer.Content>
            <PartnerListSearchForm
              status={status}
              searchText={searchText}
              onSearch={(values) => {
                setPageIndex(0);
                updateQueryParams({
                  status: values.status || '',
                  searchText: values.searchText || '',
                });
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>

        <Box className={cn('tableContainer')}>
          {partners.length === 0 ? (
            <div className={cn('emptyState')}>
              <Box flx="col" ai="center" gapSize="10px">
                <Title level="4">{t('emptyState.title', { defaultValue: 'No partners yet' })}</Title>
                <p className={cn('emptyDescription')}>
                  {t('emptyState.description', { defaultValue: 'Partners you create will appear here.' })}
                </p>
              </Box>
            </div>
          ) : (
            <Table
              id="partnersList"
              zebra
              rows={partners.map((p) => ({ id: p.PartnerAccountID, data: p }))}
              columns={[
                {
                  id: 'partnerName',
                  header: t('Table.partnerName', { defaultValue: 'Partner Name' }),
                  align: 'left',
                  template: ({ data }) => (
                    <Table.Link to={{ pathname: `/partners/${data.PartnerAccountID}` }}>{data.Name}</Table.Link>
                  ),
                },
                {
                  id: 'status',
                  header: t('Table.status', { defaultValue: 'Status' }),
                  align: 'left',
                  template: ({ data }) => (
                    <TransactionStatus
                      label={tStatus(data.AccountStatus)}
                      status={GetColorStatus(data.AccountStatus)}
                    />
                  ),
                },
                {
                  id: 'country',
                  header: t('Table.country', { defaultValue: 'Country' }),
                  field: 'Country',
                  align: 'left',
                },
              ]}
            />
          )}
        </Box>

        <Box flx="row" jc="center" mt="md" base="8">
          {pageCount > 1 && (
            <Pagination
              pageCount={pageCount}
              forcePageNumber={pageIndex}
              handlePageClick={(e) => setPageIndex(e.selected)}
            />
          )}
        </Box>
      </Card>
    </Box>
  );
}
